import { useTheme } from "@mui/material";
import { ChartCard } from "@pages:app/analytics/components/cards/ChartCard";
import { DashboardGrid } from "@pages:app/analytics/components/cards/DashboardGrid";
import {
  DonutCard,
  DonutSlice,
} from "@pages:app/analytics/components/cards/DonutCard";
import { StatCard } from "@pages:app/analytics/components/cards/StatCard";
import { AnalyticsData } from "@pages:app/analytics/tabs/useAnalyticsData";
import { useTranslation } from "react-i18next";
import { MdBarChart, MdInput, MdOutlinePeople, MdOutput } from "react-icons/md";

const NEGATIVE_COLOR = "#EE4242";

type SearchTabProps = {
  data: AnalyticsData;
};

export const SearchTab = ({ data }: SearchTabProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const iconColor = theme.palette.primary.main;
  const {
    summaryQuery,
    tokenUsageQuery,
    feedbackQuery,
    topUsersQuery,
    topDocumentsQuery,
    activeUsersChart,
    requestsChart,
    tokenUsageChart,
    topUsersSlices,
    documentSlices,
    modelSlices,
    sumSlices,
    formatCompact,
  } = data;

  const feedbackPositive = feedbackQuery.data?.positive ?? 0;
  const feedbackNegative = feedbackQuery.data?.negative ?? 0;
  const hasFeedback = feedbackPositive + feedbackNegative > 0;
  const feedbackSlices: DonutSlice[] = hasFeedback
    ? [
        {
          id: "positive",
          label: t("analytics.positive"),
          value: feedbackPositive,
          color: theme.palette.primary.main,
        },
        {
          id: "negative",
          label: t("analytics.negative"),
          value: feedbackNegative,
          color: NEGATIVE_COLOR,
        },
      ]
    : [];

  return (
    <DashboardGrid>
      {/* Rows 1, 2 */}
      <DonutCard
        title={t("analytics.feedback")}
        slices={feedbackSlices}
        centerValue={feedbackQuery.data?.total}
        centerLabel={t("analytics.unit.feedbacks")}
        loading={feedbackQuery.data?.total === undefined}
        emptyText={t("analytics.empty.feedback")}
        colSpan={6}
        rowSpan={2}
        colStart={7}
      />
      <StatCard
        label={t("analytics.activeUsers")}
        value={summaryQuery.data?.activeUsersPeriodDistinct}
        icon={<MdOutlinePeople size={22} color={iconColor} />}
        colSpan={3}
      />
      <StatCard
        label={t("analytics.requests")}
        value={summaryQuery.data?.requestsPeriod}
        icon={<MdBarChart size={22} color={iconColor} />}
        colSpan={3}
      />
      <StatCard
        label={t("analytics.inputTokens")}
        value={tokenUsageQuery.data?.inputTokens}
        icon={<MdInput size={22} color={iconColor} />}
        colSpan={3}
        format={formatCompact}
      />
      <StatCard
        label={t("analytics.outputTokens")}
        value={tokenUsageQuery.data?.outputTokens}
        icon={<MdOutput size={22} color={iconColor} />}
        colSpan={3}
        format={formatCompact}
      />

      {/* Row 3 */}
      <ChartCard
        title={t("analytics.activeUsers")}
        xAxis={activeUsersChart.xAxis}
        yAxis={activeUsersChart.yAxis}
        series={activeUsersChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />
      <ChartCard
        title={t("analytics.numberOfRequests")}
        xAxis={requestsChart.xAxis}
        yAxis={requestsChart.yAxis}
        series={requestsChart.series}
        colSpan={6}
        rowSpan={2}
        integerYTicks
      />

      {/* Row 4 */}
      <DonutCard
        title={t("analytics.usersByUsage")}
        slices={topUsersSlices}
        centerValue={sumSlices(topUsersSlices)}
        centerLabel={t("analytics.unit.requests")}
        loading={topUsersQuery.isLoading}
        emptyText={t("analytics.empty.activity")}
        colSpan={4}
      />
      <DonutCard
        title={t("analytics.referencedDocuments")}
        slices={documentSlices}
        centerValue={sumSlices(documentSlices)}
        centerLabel={t("analytics.unit.references")}
        loading={topDocumentsQuery.isLoading}
        emptyText={t("analytics.empty.documentsReferenced")}
        colSpan={4}
      />
      <DonutCard
        title={t("analytics.tokenUsageByModel")}
        slices={modelSlices}
        centerValue={sumSlices(modelSlices)}
        centerLabel={t("analytics.unit.tokens")}
        loading={tokenUsageQuery.isLoading}
        emptyText={t("analytics.empty.tokenUsage")}
        colSpan={4}
        format={formatCompact}
      />

      {/* Row 5 */}
      <ChartCard
        title={t("analytics.tokenUsage")}
        xAxis={tokenUsageChart.xAxis}
        yAxis={tokenUsageChart.yAxis}
        series={tokenUsageChart.series}
        rowSpan={2}
        hideLegend={false}
        yScale="symlog"
        yValueFormatter={formatCompact}
      />
    </DashboardGrid>
  );
};
